import React, { useEffect, useState } from 'react';
import {
    FormControl,
    Select,
    MenuItem,
    FormHelperText,
    Grid,
    SelectChangeEvent,
} from '@mui/material';
import FieldTextAreaAutoSize from '../fields/fieldTextAreaAutoSize';
import { LabelAnswer } from './labelAnswer';

type Props = {
    title?: string;
    name: any;
    answerQuestion?: any;
    onChange: (value: any) => void;
    idQuestion?: string;
    required?: boolean;
    questionType?: string;
    whyQuestion?: boolean;
    disabled?: boolean;
    dominantColor?: string;
    value?: string;
};

const FieldListAnswer = ({
    title,
    name,
    answerQuestion,
    onChange,
    idQuestion,
    required = true,
    questionType,
    whyQuestion,
    disabled,
    dominantColor,
    value: valueProp,
}: Props) => {
    const [value, setValue] = React.useState(valueProp || '');
    const [error, setError] = React.useState(false);
    const [helperText, setHelperText] = React.useState('');
    const [why, setWhy] = useState('');
    const [touched, setTouched] = useState(false);
    // Evita onChange inicial/preselección que limpiaría la cascada N2/N3
    const skipNextOnChangeRef = React.useRef(Boolean(valueProp));

    const handleSelectChange = (event: SelectChangeEvent<string>) => {
        setValue((event.target as HTMLInputElement).value);
        setHelperText(' ');
        setError(false);
        setTouched(true);
    };

    useEffect(() => {
        if (valueProp !== undefined && valueProp !== value) {
            skipNextOnChangeRef.current = true;
            // Si el padre limpia el valor (refiltro), no mostrar "obligatoria"
            if (!valueProp) {
                setTouched(false);
                setError(false);
                setHelperText(' ');
            }
            setValue(valueProp);
        }
    }, [valueProp]);

    useEffect(() => {
        const isEmpty = value.length === 0;

        if (isEmpty && touched && required) {
            setHelperText('La respuesta es obligatoria');
            setError(true);
        } else {
            setError(false);
            if (!isEmpty) setHelperText(' ');
        }

        // No disparar onChange al aplicar value externo (preselección)
        if (skipNextOnChangeRef.current) {
            skipNextOnChangeRef.current = false;
            return;
        }

        onChange({
            idQuestion,
            [name]: value,
            type: questionType,
            why: why,
            error: required && isEmpty,
        });
    }, [value, touched, why, required]);

    const optionIds = (answerQuestion || []).map((a: any) => a.id);
    const selectValue =
        value && optionIds.includes(value) ? value : '';

    return (
        <>
            <Grid item xs={12} key={idQuestion}>
                <LabelAnswer
                    text={title}
                    isRequired={required}
                    dominantColor={dominantColor}
                />
                <br />
                <FormControl error={error} fullWidth>
                    <Grid display="flex" justifyContent="center">
                        <Select
                            value={selectValue}
                            name="answer"
                            required={true}
                            disabled={disabled ?? false}
                            onChange={handleSelectChange}
                            sx={{
                                border: error
                                    ? '1px solid #d32f2f'
                                    : '1px solid #E0E0E0',
                                borderRadius: '4px',
                                width: '80%',
                                textAlign: 'left',
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: error ? '#d32f2f' : '#E0E0E0',
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                    borderColor: dominantColor || '#36c',
                                },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline':
                                    {
                                        borderColor: dominantColor || '#36c',
                                    },
                            }}
                        >
                            {answerQuestion?.map((a: any) => (
                                <MenuItem key={a.id} value={a.id}>
                                    {a.description}
                                </MenuItem>
                            ))}
                        </Select>
                    </Grid>
                    <FormHelperText sx={{ textAlign: 'center' }}>
                        {helperText}
                    </FormHelperText>
                </FormControl>
            </Grid>
            {whyQuestion && value && (
                <Grid item xs={12}>
                    <LabelAnswer
                        text="¿Por qué?"
                        isRequired={false}
                        dominantColor={dominantColor}
                    />
                    <br />
                    <FieldTextAreaAutoSize
                        name="why"
                        placeholder="Dejanos aquí las razones de porque"
                        required={false}
                        onChange={(e: any) => setWhy(e.target.value)}
                        dominantColor={dominantColor}
                    />
                </Grid>
            )}
        </>
    );
};

export default FieldListAnswer;
